import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "react-responsive";
import styled from "styled-components";
import filter from "lodash/filter";
import map from "lodash/map";
import { Title } from "@vds3/typography";
import useGlobalData from "hooks/useGlobalData";
import * as Selectors from "../ducks/selectors";
import { MarginSpacerM } from "common/components/styleTag";
import PromoBanner from "common/components/PromoBannerVDS3/PromoBannerVDS3";
import BusinessPlanCard from "./BusinessPlanCard";
import {
	areSameType,
	getCartType,
	getUniqueCategories,
	getCategoryName,
} from "../util";

const planType = "bue";

const BusinessPlans = (props) => {
	const [selectedPlanId, setSelectedPlanId] = useState(props.selected);
	const [allBYODPhones, setAllBYODPhones] = useState(false);
	const { t } = useTranslation(["plans"]);
	const isMobile = useMediaQuery({
		query: "(max-width: 545px)",
	});
	const pageData = useSelector(
		(state) => state.ProspectWidgets_WirelessPlansData
	);
	const { sessionCartData, metaInformation } = useGlobalData();
	const devicePlanList = useSelector(Selectors.devicePlanList);
	const selectedDevices = useSelector(Selectors.selectedDevices);
	const availableDevices = useSelector(Selectors.availableDevices);
	const isSelectAllChecked = useSelector(Selectors.isSelectAllChecked);
	const mappedDevices =
		(devicePlanList && devicePlanList?.map((device) => device?.key)) || [];
	const remainingList = availableDevices?.filter(
		(device) => mappedDevices?.indexOf(device?.key) == -1
	);
	const zipCode = useSelector(Selectors.zipCode);
	const plansAEM = t("plans", { ns: "plans", returnObjects: true });
	const discountedPrice =
		pageData?.deviceCompatiblePlansInfo?.configuration?.discount?.autoPayDiscount;
	const [deviceTypeInfoGroup, setDeviceTypeInfoGroup] = useState(
		pageData?.deviceTypeCompatiblePlans
	);

	useEffect(() => {
		if (props.selected) {
			setSelectedPlanId(props.selected);
		}
	}, [props.selected]);

	useEffect(() => {
		setDeviceTypeInfoGroup(pageData?.deviceTypeCompatiblePlans);
	}, [pageData?.deviceTypeCompatiblePlans]);

	useEffect(() => {
		if (planCategories.length > 1) {
		} else if (isMobile) {
			const allSameTypes =
				remainingList?.length &&
				remainingList.every((device) =>
					areSameType(device.deviceCategory, remainingList[0].deviceCategory)
				);
			if (allSameTypes) {
				const element = document.getElementById("plans-container");
				const offsetTop = element?.offsetTop;
				if (element && remainingList?.length > 0) {
					window.scrollTo({
						top: offsetTop - 50,
						behavior: "smooth",
					});
				}
			} else {
				window.scrollTo({
					top: 0,
					behavior: "smooth",
				});
			}
		} else {
			const element = document.getElementById(planCategories[0]);
			if (element && allCategories?.length > 1) {
				element.scrollIntoView({ behavior: "smooth" });
			}
		}
	}, [selectedDevices]);

	useEffect(() => {
		if (props?.devices?.length) {
			let allBYOD = true;
			for (const dev of props?.devices || []) {
				if (dev?.deviceCategory?.toLowerCase()?.includes("smartphone")) {
					allBYOD = allBYOD && dev?.byod;
				}
			}
			setAllBYODPhones(allBYOD);
		}
	}, [props?.devices]);

	const isRecommended = (planCode) =>
		plansAEM?.aggregate_plans?.plansRecommended?.length &&
		plansAEM.aggregate_plans.plansRecommended.includes(planCode);

	const selectPlanTile = (planCode, plan) => {
		const plans = Object.assign({ pricePlanCode: planCode }, plan);
		setSelectedPlanId(planCode);
		props.onPlanSelect(planCode, plans);
	};

	const getNoOfPlansApplied = (billingCode, category) => {
		if (devicePlanList?.length) {
			const plans = filter(
				devicePlanList,
				(device) =>
					device?.plan?.pricePlanCode === billingCode &&
					getCategoryName(device.deviceCategory) ===
						getCategoryName(category.deviceCategory)
			);
			if (plans) {
				return plans.length;
			}
		}
		return 0;
	};

	const planCategories = useMemo(
		() => getUniqueCategories(selectedDevices),
		[selectedDevices]
	);

	const allCategories = useMemo(
		() => getUniqueCategories(availableDevices),
		[availableDevices]
	);

	const getIsButtonEnabledByCategory = (billCode, category) => {
		let isCompatible = false;
		if (selectedDevices) {
			const allSameTypes =
				selectedDevices?.length &&
				selectedDevices.every((device) =>
					areSameType(device.deviceCategory, selectedDevices[0].deviceCategory)
				);
			isCompatible =
				allSameTypes &&
				areSameType(selectedDevices[0].deviceCategory, category.deviceCategory) &&
				pageData?.compatiblePlans?.length &&
				pageData?.compatiblePlans?.includes(billCode);
		}
		const selectedDevicesList =
			(selectedDevices && selectedDevices.map((device) => device.key)) || [];
		const loadData = remainingList.filter((data) =>
			selectedDevicesList.includes(data.key)
		).length
			? true
			: false;
		return loadData && isCompatible;
	};

	const matchPromotionByZipState = (category) => {
		/* global promos can have a key like this bueTablets_$TX_FL_MO_GA_KS_MI_UT_NC_WA_MA_PA_AZ_LA$_other_2
     en-us-plans file will have a list containing strings like TX_FL_MO_GA_KS_MI_UT_NC_WA_MA_PA_AZ_LA
     from aem we can find out if it is matching with any of the zip or state
     first will check for zip, if a match found will return the string ie ${category}_$${matchedZipStateString}$
     secondly will check for state, if a match found will return the string  ie ${category}_$${matchedZipStateString}$
     in case we want to disable this delete key from global promos and well as from en-us-plans
     */
		const tabletsZipStateList =
			plansAEM?.aggregate_plans?.specialOfferZipStates?.tablets;
		let matchedZipStateString = null;
		if (Array.isArray(tabletsZipStateList)) {
			let matchFoundForZip = null;
			tabletsZipStateList?.every((element) => {
				if (element.indexOf(zipCode) >= 0) {
					matchFoundForZip = element;
					return false;
				}
				return true;
			});
			if (!matchFoundForZip) {
				tabletsZipStateList?.every((element) => {
					if (
						element.indexOf(pageData?.deviceCompatiblePlansInfo?.billingState) >= 0
					) {
						matchedZipStateString = element;
						return false;
					}
					return true;
				});
			} else {
				matchedZipStateString = matchFoundForZip;
			}
		}
		if (matchedZipStateString) {
			return `${category}_$${matchedZipStateString}$`;
		} else {
			return category;
		}
	};

	const devicePromoCategory = (category, device) => {
		if (category === "Connected Laptops") {
			// if all plans in the laptop section are tablet plans,
			// show the tablet promo in the laptop section
			const laptopPlanList =
				deviceTypeInfoGroup?.ConnectedLaptops?.billingCode || [];
			const tabletPlansForLaptops =
				plansAEM?.aggregate_plans?.tabletPlansForLaptops || [];
			if (
				laptopPlanList?.length &&
				tabletPlansForLaptops?.length &&
				laptopPlanList?.every((plan) => tabletPlansForLaptops.includes(plan))
			) {
				return matchPromotionByZipState("Tablets");
			} else return "Laptops";
		}

		if (category === "Tablets") {
			return matchPromotionByZipState(category);
		}

		if (category?.toLowerCase()?.includes("smartphone") && device?.byod)
			return "_byod";

		return category;
	};

	const getCategoryPlans = (category) => {
		const plansArr = Object.values(props?.planDetails);
		const categoryPlans = plansArr?.filter((i) =>
			category.billingCode.includes(i?.billingCode)
		);
		const arePromosPresent = categoryPlans.some((i) => i?.promoEligible);
		const isAutopayDiscount = category?.displayName === "Smartphones";

		return categoryPlans.map((eachInfo) => (
			<BusinessPlanCard
				key={eachInfo.billingCode}
				planDetail={eachInfo}
				autoPayDiscount={isAutopayDiscount && discountedPrice}
				noOfLines={getNoOfPlansApplied(eachInfo.billingCode, category)}
				isEnabled={getIsButtonEnabledByCategory(eachInfo.billingCode, category)}
				onSelect={() => selectPlanTile(eachInfo.billingCode, eachInfo)}
				tierPrices={
					props?.staticContent?.tier_prices &&
					props?.staticContent?.tier_prices[eachInfo.billingCode]
				}
				aemFeatures={
					plansAEM?.planFeatures && plansAEM.planFeatures[eachInfo.billingCode]
				}
				isRecommended={isRecommended(eachInfo.billingCode)}
				promoSpacing={arePromosPresent}
			/>
		));
	};

	const getCategoryPromo = (category, device = {}) => {
		const promoKey =
			planType +
			devicePromoCategory(category.deviceCategory, device) +
			`_${getCartType(sessionCartData)}` +
			"_2";
		if (
			metaInformation?.globalPromos?.plans &&
			metaInformation.globalPromos?.plans[promoKey] &&
			metaInformation.globalPromos?.plans[promoKey]?.active
		) {
			return (
				<>
					<PromoBanner
						bgColor={"black"}
						icon="tag"
						promoData={metaInformation?.globalPromos?.plans?.[promoKey]}
					/>
					<MarginSpacerM />
				</>
			);
		} else return <></>;
	};

	return (
		<div className="businessplanselection">
			{props.isFromPlansNew
				? getCategoryPromo(props.category, props.device)
				: deviceTypeInfoGroup &&
				  Object.keys(deviceTypeInfoGroup).length > 0 &&
				  map(deviceTypeInfoGroup, (category) => {
						const isNotAllSelected =
							!isSelectAllChecked && availableDevices?.length
								? availableDevices.filter(
										(data) => data.deviceCategory === category.deviceCategory
								  )
								: [];
						if (
							category.billingCode.length > 0 &&
							(isSelectAllChecked || isNotAllSelected.length)
						) {
							return (
								<div id={category.displayName} style={{ scrollMarginTop: "1rem" }}>
									<SectionTitle>
										<Title size="small" bold>
											{category.displayName}
										</Title>
									</SectionTitle>
									{getCategoryPromo(category)}
									{props?.planDetails && (
										<PlanGrid>{getCategoryPlans(category)}</PlanGrid>
									)}
								</div>
							);
						}
				  })}
		</div>
	);
};

const SectionTitle = styled.div`
	padding-bottom: 1rem;
`;

export const PlanGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(290px, 290px));
	column-gap: 1.5rem;
	justify-content: center;
	row-gap: 1rem;
	margin-bottom: 2.5rem;
	@media (max-width: 545px) {
		margin-bottom: 2rem;
		padding-left: 0px !important;
		padding-right: 0px !important;
	}
`;

export default BusinessPlans;
